import {
  ProductsRepository,
  Either,
  ProductsFailure,
  Product,
  right,
  left,
  Size,
  Sort,
  GetProdutcsResult,
  NewProduct,
  EditProduct,
  EditProductAttribute,
  NewProductAttribute,
} from "../../domain";
import { ProductsRemoteDataSourceError } from "../core";
import { ProductsRemoteDataSource } from "../data-sources";

export class ProductsRepositoryImpl implements ProductsRepository {
  constructor(private readonly remoteDataSource: ProductsRemoteDataSource) {}

  private handleError(error: Error): ProductsFailure {
    const message = error.message as ProductsRemoteDataSourceError;
    if (message === "NetworkError") return ProductsFailure.networkFailure();
    if (message === "CategoryNotFoundError")
      return ProductsFailure.categoryNotFoundFailure();
    if (message === "ProductNotFoundError")
      return ProductsFailure.productNotFoundFailure();
    return ProductsFailure.serverFailure();
  }

  public async getProductInfo(
    id: string
  ): Promise<Either<ProductsFailure, Product>> {
    try {
      const res = await this.remoteDataSource.getProductInfo(id);
      return right(res);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async getSizes(): Promise<Either<ProductsFailure, Size[]>> {
    try {
      const res = await this.remoteDataSource.getSizes();
      return right(res);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async getProducts(
    page: number,
    sort: Sort
  ): Promise<Either<ProductsFailure, GetProdutcsResult>> {
    try {
      const res = await this.remoteDataSource.getProducts(page, sort);
      return right(res);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async add(
    product: NewProduct
  ): Promise<Either<ProductsFailure, null>> {
    try {
      await this.remoteDataSource.add(product);
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async delete(id: string): Promise<Either<ProductsFailure, null>> {
    try {
      await this.remoteDataSource.remove(id);
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async editProductInfo(
    product: EditProduct
  ): Promise<Either<ProductsFailure, null>> {
    try {
      await this.remoteDataSource.editProductInfo(product);
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async editProductAttribute(
    attribute: EditProductAttribute
  ): Promise<Either<ProductsFailure, null>> {
    try {
      await this.remoteDataSource.editProductAttribute(attribute);
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async addAttributeToProduct(
    productId: string,
    attribute: NewProductAttribute
  ): Promise<Either<ProductsFailure, null>> {
    try {
      await this.remoteDataSource.addAttributeToProduct(productId, attribute);
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async setProductAttributesPrice(
    productId: string,
    price: number
  ): Promise<Either<ProductsFailure, null>> {
    try {
      await this.remoteDataSource.setProductAttributesPrice(productId, price);
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async setProductAttributesOffPrice(
    productId: string,
    offPrice: number
  ): Promise<Either<ProductsFailure, null>> {
    try {
      await this.remoteDataSource.setProductAttributesOffPrice(
        productId,
        offPrice
      );
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async setProductAttributesCount(
    productId: string,
    count: number
  ): Promise<Either<ProductsFailure, null>> {
    try {
      await this.remoteDataSource.setProductAttributesCount(productId, count);
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async addImageToProduct(
    productId: string,
    image: File,
    index: number
  ): Promise<Either<ProductsFailure, null>> {
    try {
      await this.remoteDataSource.addImageToProduct(productId, image, index);
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async deleteImageFromProduct(
    imageId: string
  ): Promise<Either<ProductsFailure, null>> {
    try {
      await this.remoteDataSource.deleteImageFromProduct(imageId);
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async editProductImage(
    imageId: string,
    image: File
  ): Promise<Either<ProductsFailure, null>> {
    try {
      await this.remoteDataSource.editProductImage(imageId, image);
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }
}
